<template>
  <Overlay
    :visible="visible"
    :title="isEdit ? 'Edit NFT Listing' : 'List NFT for Sale'"
    @close="handleClose"
  >
    <div class="list-nft-form">
      <!-- NFT Preview -->
      <div>
        <div class="nft-preview" v-if="nft">
          <div
            v-if="nft.image || nft.thumbnail"
            class="nft-image"
            :style="{ backgroundImage: `url(${nft.image || nft.thumbnail})` }"
          ></div>
          <div
            v-else
            class="nft-image nft-placeholder"
            :class="placeholderClass"
          >
            <span class="placeholder-text">{{ placeholderText }}</span>
          </div>
          <div class="nft-info">
            <h3 class="nft-name">{{ nft.name || `NFT #${nft.tokenId}` }}</h3>
            <p
              class="collection-name"
              v-if="nft.collection || nft.collectionName"
            >
              {{ nft.collection || nft.collectionName }}
            </p>
            <p class="token-id gray">Token ID: {{ nft.tokenId }}</p>
            <div class="network-info">
              <span class="network-badge gray">On {{ networkName }}</span>
            </div>
          </div>
        </div>

        <!-- Network Info -->
      </div>
      <!-- Price Input -->
      <div class="price-input-section">
        <label for="listingPrice" class="price-label">
          Listing Price in {{ nativeCurrency }}
        </label>
        <input
          id="listingPrice"
          type="number"
          v-model.number="listingPrice"
          :placeholder="`e.g., 0.05 ${nativeCurrency}`"
          step="0.001"
          required
          class="price-input alignCentre"
          ref="priceInput"
          @input="validatePrice"
        />
        <p v-if="!isPriceValid && listingPrice > 0" class="error-text">
          Price must be between 0.001 and 1000 {{ nativeCurrency }}
        </p>
      </div>

      <!-- Fee Breakdown (Read-only) -->
      <div class="economics-table-wrapper">
        <table class="mint-economics-table">
          <tbody>
            <tr>
              <th>Listing Price</th>
              <td>
                {{ nativeCurrency }}
                <span class="monospaced-value">{{ listingPrice || "0" }}</span>
              </td>
            </tr>
            <tr>
              <th>Creator Royalty ({{ royaltyPercent }}%)</th>
              <td>
                {{ nativeCurrency }}
                <span class="monospaced-value">{{
                  creatorRoyaltyDisplay
                }}</span>
              </td>
            </tr>
            <tr>
              <th>Platform Fee ({{ platformFeePercent }}%)</th>
              <td>
                {{ nativeCurrency }}
                <span class="monospaced-value">{{ platformFeeDisplay }}</span>
              </td>
            </tr>
            <tr class="fee-total">
              <th><strong>You'll Receive</strong></th>
              <td>
                <strong
                  >{{ nativeCurrency }}
                  <span class="monospaced-value">{{
                    netProceedsDisplay
                  }}</span></strong
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Terms Acknowledgment -->
      <section class="modal-section agreement-section">
        <label class="agreement-checkbox">
          <input type="checkbox" v-model="termsAcknowledged" />
          <span>
            I understand this listing is on-chain and buyers will pay in
            {{ nativeCurrency }}
          </span>
        </label>
      </section>

      <!-- Action Buttons -->
      <div class="row alignCentreRow button-row">
        <UnitButton
          type="button"
          variant="secondary"
          class="clickableHeight"
          @click="handleClose"
          :disabled="isListing"
          v-haptic
        >
          Cancel
        </UnitButton>
        <UnitButton
          type="button"
          @click="handleListNFT"
          :disabled="!canList"
          :loading="isListing"
          loading-label="Processing..."
          v-haptic
        >
          {{ isEdit ? "Update Listing" : "List NFT" }}
        </UnitButton>
      </div>
    </div>
  </Overlay>
</template>

<script>
/**
 * ListNFTOverlay - NFT Listing Form Component
 *
 * Allows NFT owners to list their NFTs for sale on the marketplace with blockchain-native pricing.
 *
 * FEE BREAKDOWN EXPLAINED:
 * ========================
 * The economics table displays three fee calculations based on the user's entered listing price:
 *
 * 1. CREATOR ROYALTY (5.00% default)
 *    - Source: nftData.value?.royaltyBps || 500
 *    - This is read from the NFT's metadata field 'royaltyBps'
 *    - 'Bps' = Basis Points (1% = 100 bps, so 500 bps = 5%)
 *    - This royalty was set by the ORIGINAL CREATOR when they minted the NFT
 *    - It goes to the original creator on EVERY resale (not the current seller)
 *    - Calculation: (listingPrice × 500) / 10000 = listingPrice × 0.05
 *
 * 2. PLATFORM FEE (7.00% hardcoded)
 *    - Source: platformFeeBps = 700 (hardcoded in useNFTListing.js)
 *    - This is Unit's marketplace fee for facilitating the sale
 *    - 700 bps = 7%
 *    - This goes to the Unit platform
 *    - Calculation: (listingPrice × 700) / 10000 = listingPrice × 0.07
 *
 * 3. YOU'LL RECEIVE (Net proceeds)
 *    - This is what the SELLER receives after deducting both fees
 *    - Calculation: listingPrice × (1 - (500 + 700) / 10000)
 *    - Example: If listing for 1 ETH:
 *      * Creator Royalty: 1 × 0.05 = 0.05 ETH
 *      * Platform Fee: 1 × 0.07 = 0.07 ETH
 *      * Seller Receives: 1 - 0.05 - 0.07 = 0.88 ETH (88%)
 *
 * WHERE THE DATA COMES FROM:
 * ==========================
 * - All calculations are done in src/composables/useNFTListing.js
 * - The composable receives nftData from the parent (NFTGallery.vue)
 * - The NFT's royaltyBps is stored in Firestore when the NFT is minted
 * - If royaltyBps is missing, it defaults to 500 (5%)
 *
 * DISPLAY FORMAT:
 * ===============
 * - Numbers are right-aligned (text-align: right)
 * - Currency symbol BEFORE the number (e.g., "ETH 0.0500")
 * - Numbers use monospaced font (var(--font-family-mono))
 * - All amounts display 4 decimal places for precision
 */
import { ref, computed, watch, onMounted } from "vue";
import { auth } from "@/firebase";
import Overlay from "@/components/GenericComponents/Overlay.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import { useNFTListing } from "@/composables/useNFTListing";

export default {
  name: "ListNFTOverlay",
  components: {
    Overlay,
    UnitButton,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    nft: {
      type: Object,
      required: false, // Changed to false to allow null during close
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    existingPrice: {
      type: Number,
      default: 0,
    },
  },
  emits: ["close", "listed", "updated"],
  setup(props, { emit }) {
    const priceInput = ref(null);
    const currentUserUid = ref(auth.currentUser ? auth.currentUser.uid : null);
    const nftData = computed(() => props.nft);

    const {
      isListing,
      listingPrice,
      termsAcknowledged,
      chainId,
      nativeCurrency,
      networkName,
      royaltyPercent,
      platformFeePercent,
      creatorRoyaltyDisplay,
      platformFeeDisplay,
      netProceedsDisplay,
      isPriceValid,
      canList,
      listNFT,
      updateListing,
    } = useNFTListing({ currentUserUid, nftData });

    // Generate placeholder for NFTs without images (same as NFTGallery)
    const generateNFTPlaceholder = (nft) => {
      const colorIndex =
        parseInt(nft.contractAddress?.slice(-1) || "0", 16) % 10;
      return {
        placeholderClass: `nft-placeholder-${colorIndex}`,
        placeholderText: nft.name || `#${nft.tokenId}`,
        colorIndex: colorIndex,
      };
    };

    const placeholderData = computed(() => {
      if (!props.nft) return null;
      return generateNFTPlaceholder(props.nft);
    });

    const placeholderClass = computed(
      () => placeholderData.value?.placeholderClass || ""
    );
    const placeholderText = computed(
      () => placeholderData.value?.placeholderText || ""
    );

    // Initialize price if editing
    watch(
      () => props.visible,
      (visible) => {
        if (visible && props.isEdit && props.existingPrice > 0) {
          listingPrice.value = props.existingPrice;
        } else if (visible && !props.isEdit) {
          listingPrice.value = 0;
          termsAcknowledged.value = false;
        }

        // Focus price input when opened
        if (visible) {
          setTimeout(() => {
            if (priceInput.value) {
              priceInput.value.focus();
            }
          }, 100);
        }
      }
    );

    const validatePrice = () => {
      // Validation is handled by computed isPriceValid
    };

    const handleListNFT = async () => {
      const nftId = `nft-${props.nft.chainId}-${props.nft.contractAddress}-${props.nft.tokenId}`;

      let result;
      if (props.isEdit) {
        result = await updateListing(nftId);
        if (result.success) {
          emit("updated", { nftId, price: listingPrice.value });
          emit("close");
        }
      } else {
        result = await listNFT();
        if (result.success) {
          emit("listed", { nftId, timelineDocId: result.timelineDocId });
          emit("close");
        }
      }
    };

    const handleClose = () => {
      emit("close");
    };

    return {
      priceInput,
      isListing,
      listingPrice,
      termsAcknowledged,
      nativeCurrency,
      networkName,
      royaltyPercent,
      platformFeePercent,
      creatorRoyaltyDisplay,
      platformFeeDisplay,
      netProceedsDisplay,
      isPriceValid,
      canList,
      placeholderClass,
      placeholderText,
      validatePrice,
      handleListNFT,
      handleClose,
    };
  },
};
</script>

<style scoped>
.list-nft-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-5);
}

/* NFT Preview */
.nft-preview {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm);
}

.nft-image {
  width: 150px;
  height: 150px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* NFT Placeholder (same as NFTGallery/ImageCard) */
.nft-image.nft-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.placeholder-text {
  font-size: 0.9rem;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  padding: var(--space-2);
  word-wrap: break-word;
  max-width: 90%;
}

/* Color variants for NFT placeholders */
.nft-placeholder-0 {
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
}
.nft-placeholder-1 {
  background: linear-gradient(135deg, #4ecdc4, #6eddd6);
}
.nft-placeholder-2 {
  background: linear-gradient(135deg, #45b7d1, #67c3dd);
}
.nft-placeholder-3 {
  background: linear-gradient(135deg, #96ceb4, #a8d5c4);
}
.nft-placeholder-4 {
  background: linear-gradient(135deg, #ffeaa7, #ffefb7);
}
.nft-placeholder-5 {
  background: linear-gradient(135deg, #dda0dd, #e6b3e6);
}
.nft-placeholder-6 {
  background: linear-gradient(135deg, #98d8c8, #a8ddd3);
}
.nft-placeholder-7 {
  background: linear-gradient(135deg, #f7dc6f, #f9e285);
}
.nft-placeholder-8 {
  background: linear-gradient(135deg, #bb8fce, #c7a2d6);
}
.nft-placeholder-9 {
  background: linear-gradient(135deg, #85c1e9, #97caf0);
}

/* Adjust text color for lighter backgrounds */
.nft-placeholder-4 .placeholder-text,
.nft-placeholder-7 .placeholder-text {
  color: #333;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
}

.nft-info {
  flex: 1;
  min-width: 0;
}

.nft-name {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--font-size-body);
  font-weight: 600;
}

.collection-name {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
}

.token-id {
  margin: 0;
  font-size: var(--font-size-label);
}

/* Network Info */
.network-info {
  display: flex;
  gap: var(--space-4);
  align-items: center;
}

.network-badge,
.currency-badge {
  padding: var(--space-2) 0;
  color: var(--color-text-on-accent);
  border-radius: var(--radius-pill);
  font-size: var(--font-size-label);
}

.currency-badge {
  background: var(--color-accent-primary);
  color: var(--color-text-on-accent);
}

/* Price Input */
.price-input-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
}

.price-label {
  font-size: var(--font-size-label);
  margin-bottom: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.price-input {
  font-size: var(--font-size-body);
  padding: var(--space-4);
  text-align: center;
  font-feature-settings: "tnum";
  width: auto;
  max-width: 300px;
}

.price-hint {
  text-align: center;
  font-size: var(--font-size-label);
}

.error-text {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
  margin: 0;
}

/* Economics Table (same as MintPreviewModal) */
.economics-table-wrapper {
  margin-top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.mint-economics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.mint-economics-table th,
.mint-economics-table td {
  text-align: left;
  background-color: var(--color-surface-canvas);
  padding: var(--space-4) 0;
}

.mint-economics-table tr:first-child th,
.mint-economics-table tr:first-child td {
  border-top: 1px solid var(--color-border-subtle);
}

.mint-economics-table th {
  font-weight: 600;
  color: var(--color-text-muted);
  padding-right: var(--space-2);
}

.mint-economics-table td {
  font-feature-settings: "tnum";
}

.mint-economics-table .monospaced-value {
  font-family: var(--font-family-mono);
  font-feature-settings: "tnum";
}

.mint-economics-table tr.fee-total th,
.mint-economics-table tr.fee-total td {
  border-top: 2px solid var(--color-border-subtle);
  padding-top: var(--space-5);
  font-size: var(--font-size-body);
}

/* Agreement Section (same as MintPreviewModal) */
.modal-section {
  margin-bottom: var(--space-5);
}

.agreement-section {
  border-top: 1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.08));
  padding-top: var(--space-5);
}

.agreement-checkbox {
  display: flex;
  gap: var(--space-4);
  justify-content: flex-start;
  align-items: flex-start;
}

.agreement-checkbox input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
  width: 20px;
}

.agreement-checkbox span {
  font-size: var(--font-size-body);
  line-height: 1.4;
}

/* Buttons */
.button-row {
  gap: var(--space-5);
  margin-top: var(--space-4);
  width: fit-content;
  align-self: flex-end;
}

.button-row button {
  flex: 1;
}
</style>
